import React, { useState } from 'react';
import { Card, Button, Table, Badge, Spinner, Nav, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { KIEMTRASOLIEU_API } from '../../../../utils/apiUrl';
import { trangThaiKiemTraSoLieuValue } from '../../../../utils/define';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';
import moment from 'moment';
import KiemTraSoLieuAdd from './KiemTraSoLieuAdd';
import KiemTraSoLieuEdit from './KiemTraSoLieuEdit';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const renderTooltip = (props, label) => (
    <Tooltip id="button-tooltip" {...props}>
        {label}
    </Tooltip>
);

const KiemTraSoLieu = ({ hoSoId, activeTab, setActiveTab }) => {
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit'
    const [dataEdit, setDataEdit] = useState(null);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const { isLoading, data: queryData, refetch } = useQuery(
        ['kiemTraSoLieuData', hoSoId],
        () => Get(`${KIEMTRASOLIEU_API.GET_BY_HOSO_ID}/${hoSoId}`),
        { 
            enabled: !!hoSoId && viewMode === 'list',
            keepPreviousData: true
        }
    );

    // Xử lý data từ API
    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else {
            dataList = [queryData];
        }
    }

    const deleteFunc = useMutation((id) => DeleteSingle(KIEMTRASOLIEU_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setViewMode('add');
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setViewMode('edit');
    };

    const handleView = (item) => {
        setDataEdit(item);
        setViewMode('view');
    };

    const deleteAction = (id) => {
        setIdWantDelete(id);
        setConfirmDelMessage('Bản ghi kiểm tra số liệu này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.success)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['kiemTraSoLieuData', hoSoId]);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiKiemTraSoLieuValue.choDuyet: return <Badge variant="warning">Chờ duyệt</Badge>;
            case trangThaiKiemTraSoLieuValue.boSung: return <Badge variant="info">Yêu cầu chỉnh sửa</Badge>;
            case trangThaiKiemTraSoLieuValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            case trangThaiKiemTraSoLieuValue.tuChoi: return <Badge variant="danger">Từ chối</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="flex-row">
                        <Nav.Item>
                            <Nav.Link eventKey="noiLaySoLieu" className="font-weight-bold">Nơi lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="quaTrinhLaySoLieu" className="font-weight-bold">Quá trình lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="kiemTraSoLieu" className="font-weight-bold">Kiểm tra số liệu</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Button variant="success" size="sm" onClick={handleAdd}>
                        <i className="fas fa-plus mr-1"></i> Thêm mới
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th className="text-center" width="50">STT</th>
                                        <th className="text-center">Số lượng bài báo</th>
                                        <th>Các tạp chí</th>
                                        <th className="text-center">Ngày dự kiến kiểm tra</th>
                                        <th className="text-center">Trạng thái</th>
                                        <th>Ghi chú</th>
                                        <th className="text-center" width="120">Thao tác</th>
                                    </tr>
                                </thead>
                            <tbody>
                                {dataList.length > 0 ? (
                                    dataList.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="text-center">{index + 1}</td>
                                            <td className="text-center">{item.soLuongBaiBao || 0}</td>
                                            <td>{item.trenCacTapChi}</td>
                                            <td className="text-center">
                                                {item.ngayKiemTra ? moment(item.ngayKiemTra).format('DD/MM/YYYY') : ''}
                                            </td>
                                            <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                            <td>{item.ghiChuChinhSua || ''}</td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center text-nowrap">
                                                    <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Xem chi tiết')}>
                                                        <Button variant="info" size="sm" onClick={() => handleView(item)} className="mr-1 text-white">
                                                            <i className="fas fa-eye"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Sửa')}>
                                                        <Button 
                                                            variant="warning" 
                                                            size="sm" 
                                                            className="mr-1 text-white" 
                                                            onClick={() => handleEdit(item)}
                                                            disabled={item.trangThai !== trangThaiKiemTraSoLieuValue.choDuyet && item.trangThai !== trangThaiKiemTraSoLieuValue.boSung}
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Xóa')}>
                                                        <Button 
                                                            variant="danger" 
                                                            size="sm" 
                                                            onClick={() => deleteAction(item.id)}
                                                            disabled={item.trangThai !== trangThaiKiemTraSoLieuValue.choDuyet && item.trangThai !== trangThaiKiemTraSoLieuValue.boSung}
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            Chưa có dữ liệu Kiểm tra số liệu
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                        </div>
                        <ConfirmModal
                            isOpen={openConfirmDeleteModal}
                            confirmType="delete"
                            goBackAfterDelete={false}
                            handleAccept={acceptDelete}
                            handleCancel={cancelDelete}
                            content={confirmDelMessage}
                        />
                    </>
                )}
            </Card.Body>
        </Card>

        {viewMode === 'add' && <KiemTraSoLieuAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />}
        {viewMode === 'edit' && dataEdit && <KiemTraSoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} isView={false} isUserView={true} />}
        {viewMode === 'view' && dataEdit && <KiemTraSoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} isView={true} isUserView={true} />}
        </>
    );
};

export default KiemTraSoLieu;
